import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaShoppingBag } from 'react-icons/fa';
import { FiPlus, FiMinus } from 'react-icons/fi';
import { getData, postData } from '@/services/api.service';
import { end_points } from '@/services/endpoints';
import { addCartItem } from '@/core/redux/cartSlice';
import { useDispatch, useSelector } from 'react-redux';
import { isLoggedIn } from '@/utils/constants';
import { toast } from 'react-toastify';
import store, { RootState } from '@/core/redux/store';
import { routes } from '@/utils/routes/routes';
import Spinner from './Components/spinner';
import ProductImageSlider from './productImageSlider';

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState<any>(null);
  const [productPrice, setProductPrice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState<string | null>('');
  const user = store.getState().common.userDetails;
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currencyCode = useSelector(
    (state: RootState) => state.common.header?.currency?.symbol
  );

  const isLoggedIn = store.getState().common.isLoading;
  const getProductDetail = async () => {
    setLoading(true);
    try {
      const payload = { id: id, type: 'service' };
      const response = await getData(end_points.productDetail.url, payload);
      if (response.status === 200) {
        setProduct(response.data.data.product);
        setProductPrice(response.data.data.product.price);
      }
    } catch (error: any) {
      if (error?.data?.error_message == 'not_found') {
        navigate(routes.home);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductDetail();
  }, [id]);

  const handleVariantClick = (variantName: string, price: number) => {
    setProductPrice(product?.price + price);
    setSelectedVariant(variantName);
  };

  const handleIncrement = () => {
    if (quantity < product?.qty) {
      const updatedQty = quantity + 1;
      setQuantity(updatedQty);
    } else {
      toast.warning('Maximum stock reached!');
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      const updatedQty = quantity - 1;
      setQuantity(updatedQty);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= product?.qty) {
      setQuantity(value);
    }
  };

  const addToCart = () => {
    const AddToCartProduct = {
      user_id: user?.id,
      id: product?.id,
      name: product?.name,
      price: productPrice,
      image: product?.images[0],
      category_id: product?.category_id,
      quantity: quantity,
      variant: selectedVariant,
      service_mode: product?.service_mode,
    };

    dispatch(addCartItem(AddToCartProduct));
    navigate(routes.cart);
  };

  return (
    <section className="product-details-section">
      {loading ? (
        <Spinner />
      ) : (
        <div className="container">
          <div className="row mt-4">
            <div className="col-md-12">
              <div className="card-box">
                <div className="row">
                  <div className="col-md-5">
                    <div className="slider-wrapper">
                      <div className="slider-for slick-initialized slick-slider">
                        <div className="slick-list draggable">
                          <div className="slick-track">
                            <div
                              className="slider-for__item ex1 slick-slide slick-current slick-active"
                              data-src={product?.images[0]}
                            >
                              {product?.images.length > 1 ? (
                                <ProductImageSlider product={product} />
                              ) : (
                                <img src={product?.images[0]} alt="image" className='prod-img-lg' />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <Link className="prod-h2" to="#">
                      {product?.name}
                    </Link>
                    <div className="item-price mb-3 mt-2">
                      <span className="price">
                        <span className='curr-symbol'>{currencyCode}</span>{productPrice}
                      </span>
                    </div>
                    {product?.variant_values?.length > 0 && (
                      <div className="row" id="variant-input-section">
                        <div className="col-md-2 form-group mt-2">
                          <label>{product?.variants[0]}</label>
                        </div>
                        <div className="col-md-10 form-group mt-2">
                          <ul className="d-flex list-unstyled flex-wrap gap-2">
                            {product?.variant_values?.map(
                              (variant: any, index: number) => {
                                const name = variant?.variant?.name;
                                const isActive = selectedVariant === name;
                                const price = variant?.additional_price;
                                return (
                                  <li
                                    key={index}
                                    className={`variant_val px-3 py-1 border rounded ${isActive
                                        ? 'bg-primary text-white'
                                        : 'bg-light text-dark'
                                      }`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleVariantClick(name, price)}
                                  >
                                    {name}
                                  </li>
                                );
                              }
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                    {product?.qty == 0 && product?.type !== 'service' ? (
                      <h4>Out of stock</h4>
                    ) : (
                      <div className="item-options mb-5">
                        <form
                          method="post"
                          id="add_to_cart"
                          className="mt-3 mb-3 d-flex"
                        >
                          <div className="input-qty">
                            <button
                              type="button"
                              className="quantity-left-minus"
                              onClick={handleDecrement}
                            >
                              <FiMinus />
                            </button>
                            <input
                              type="number"
                              name="qty"
                              className="input-number"
                              value={quantity}
                              min={1}
                              max={product?.qty}
                              onChange={handleInputChange}
                            />
                            <button
                              type="button"
                              className="quantity-right-plus"
                              onClick={handleIncrement}
                            >
                              <FiPlus />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="button style1 add-to-cart"
                            onClick={addToCart}
                          >
                            <FaShoppingBag className="mr-2" />
                            Add to cart
                          </button>
                        </form>
                      </div>
                    )}
                    <hr />
                    <div className="mt-5">
                      <span className="d-inline-block">SKU</span>
                      <ul className="footer-social d-inline">
                        <li>:</li>
                        <li>{product?.code}</li>
                      </ul>
                    </div>
                    {product?.tags != null && (
                      <div className="mt-2">
                        <span className="d-inline-block mt-3">Tags</span>
                        <ul className="footer-social d-inline">
                          <li>:</li>
                          <li>{product?.tags}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="col-12 ">
                    <div className="item-description-card">
                      <h2>Description</h2>
                      <div
                        className="item-description mb-3"
                        dangerouslySetInnerHTML={{ __html: product?.product_details }}
                      />
                      <div
                        className="item-description"
                        dangerouslySetInnerHTML={{ __html: product?.meta_description }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductDetails;
